import React, { useContext } from 'react';
import { productionTenants } from '../../../../common/src/domain/tenant/tenants';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet, fetchPost } from '../../store/fetch';
import { appContext } from '../appContext';
import {
    AlertMicro,
    EditPanelButton,
    EditPanelButtons,
    EditPanelInput,
    EditPanelInputPassword,
    EditPanelSection,
    LinkButton,
    LoginHeader,
    PanelBody,
} from '../elements';
import { Logo } from '../Logo';
import { Component } from '../utils/Component';
import { Login } from './Login';

const zxyCloudAdminHostname = 'app.zxy-cloud.com';

function SoftwareVersion() {
    const currentAppContext = useContext(appContext.context);
    const { softwareVersion } = currentAppContext.globalSettings;

    return (
        <div className="mt10 mr10 fs8 fw200 text-right">
            <p>{softwareVersion}</p>
        </div>
    );
}

interface ILoginContainerProps extends ITranslateProps {
    tempLogin: boolean;
    passwordToken: string | null;
}

interface ILoginState {
    userEmail: string;
    password: string;
    repeatPassword?: undefined | string;
    code?: undefined | string;
    validationError: boolean;
    tokenError: boolean;
    form: 'login' | 'forgetPassword' | 'setPassword' | 'tenants';
    submitted: boolean;
    response: boolean;
}

function shouldShowTenants() {
    if (window.location.search.includes('showTenants')) {
        return true;
    }
    return window.location.hostname.toLowerCase() === zxyCloudAdminHostname && !window.location.search.includes('showLogin');
}

class LoginContainerComponent extends Component<ILoginContainerProps, ILoginState> {
    constructor(props: ILoginContainerProps) {
        super(props);

        this.state = {
            userEmail: '',
            password: '',
            form: 'login',
            submitted: false,
            response: false,
            validationError: false,
            tokenError: false,
        };
    }

    public override async componentDidMount() {
        if (this.props.passwordToken) {
            await this.validateToken();
            await this.setState({ form: 'setPassword' });
        } else if (shouldShowTenants()) {
            await this.setState({ form: 'tenants' });
        }
    }

    public override render() {
        return (
            <div className="external-page external-alt">
                <section id="content">
                    <div className="admin-form theme-info mw500" id="login">
                        <div className="row table-layout">
                            <Logo />
                        </div>

                        {/* Tenants */}
                        {this.state.form === 'tenants' && (
                            <div className="panel mt30 mb25">
                                <table className="table ">
                                    <tbody>
                                        {productionTenants
                                            .filter(({ isVisibleOnLandingPage }) => isVisibleOnLandingPage)
                                            .map((tenant) => (
                                                <tr key={tenant.name}>
                                                    <th> {tenant.fullName}</th>
                                                    <td>
                                                        {' '}
                                                        <a href={tenant.loginUrl}>{tenant.loginUrl}</a>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Login form */}
                        {this.state.form === 'login' && (
                            <Login
                                tempLogin={this.props.tempLogin}
                                navigateToForgetPassword={async () => this.setState({ form: 'forgetPassword', submitted: false })}
                            />
                        )}

                        {/* Forgot password form */}
                        {this.state.form === 'forgetPassword' && (
                            <>
                                <div className="panel mt30 mb25">
                                    {this.state.response && !this.state.validationError && (
                                        <AlertMicro text="A link to reset your password has been sent to your e-mail address. " severity="info" />
                                    )}
                                    {this.state.validationError && this.state.code !== '' && (
                                        <AlertMicro text={this.props.t(`validation.${this.state.code}`)} severity="danger" />
                                    )}
                                    <LoginHeader>
                                        Forgot your password?
                                        <br />
                                        Don’t worry, it happens to all of us.
                                    </LoginHeader>
                                    <form
                                        method="get"
                                        action="/"
                                        id="admin-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void this.forgetPasswordSubmit();
                                        }}
                                    >
                                        <PanelBody>
                                            <p>Please enter your e-mail below and we will send you a mail with a link to reset your password. </p>
                                            <label htmlFor="useremail" className="field-label text-muted fs18 mb10">
                                                {this.props.t('common.emailAddress')}
                                            </label>
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="userEmail"
                                                    placeholder={this.props.t('login.enterEmailAddress')}
                                                    autoComplete="email"
                                                    notValid={this.state.validationError}
                                                    value={this.state.userEmail}
                                                    icon="fa fa-user"
                                                    handleChange={(value) => this.handleChange(value, 'userEmail')}
                                                    handleEnter={async () => this.forgetPasswordSubmit()}
                                                />
                                            </EditPanelSection>
                                        </PanelBody>
                                        <EditPanelButtons footer={true}>
                                            <EditPanelButton
                                                primary={true}
                                                disabled={this.state.submitted}
                                                text={this.props.t('common.submit')}
                                                onClick={async () => this.forgetPasswordSubmit()}
                                            />
                                        </EditPanelButtons>
                                    </form>
                                </div>
                                {this.loginFooter()}
                            </>
                        )}

                        {/* Reset password form */}
                        {this.state.form === 'setPassword' && (
                            <>
                                <div className="panel mt30 mb25">
                                    {this.state.response && !this.state.validationError && (
                                        <AlertMicro
                                            text="Your password has been saved. You will be redirected to the login screen or click 'Login' below to log in with your new password"
                                            severity="info"
                                        />
                                    )}
                                    {((this.state.response && this.state.code !== '') || this.state.tokenError) && (
                                        <AlertMicro text={this.props.t(`validation.${this.state.code}`)} severity="danger" />
                                    )}
                                    <LoginHeader>Set a password for your account</LoginHeader>
                                    <form
                                        method="get"
                                        action="/"
                                        id="admin-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void this.newPasswordSubmit();
                                        }}
                                    >
                                        <PanelBody>
                                            <p>The new password must contain at least 8 characters and must contain at least a punctuation mark.</p>
                                            <EditPanelSection>
                                                <label htmlFor="password" className="field-label text-muted fs18 mb10">
                                                    {this.props.t('common.password')}
                                                </label>
                                                <EditPanelInputPassword
                                                    name="password"
                                                    autoComplete="new-password"
                                                    notValid={this.state.validationError}
                                                    placeholder={this.props.t('profile.newPassword')}
                                                    value={this.state.password}
                                                    handleChange={(value) => this.handleChange(value, 'password')}
                                                    handleEnter={async () => this.newPasswordSubmit()}
                                                />
                                            </EditPanelSection>
                                            <EditPanelSection>
                                                <label htmlFor="password" className="field-label text-muted fs18 mb10">
                                                    {this.props.t('profile.repeatNewPassword')}
                                                </label>
                                                <EditPanelInputPassword
                                                    name="repeatPassword"
                                                    autoComplete="new-password"
                                                    notValid={this.state.validationError}
                                                    placeholder={this.props.t('profile.repeatNewPassword')}
                                                    value={this.state.repeatPassword}
                                                    handleChange={(value) => this.handleChange(value, 'repeatPassword')}
                                                    handleEnter={async () => this.newPasswordSubmit()}
                                                />
                                            </EditPanelSection>
                                        </PanelBody>
                                        <EditPanelButtons footer={true}>
                                            <EditPanelButton
                                                primary={true}
                                                text="Activate"
                                                disabled={this.state.submitted && !this.state.validationError}
                                                onClick={async () => this.newPasswordSubmit()}
                                            />
                                        </EditPanelButtons>
                                    </form>
                                </div>
                                {this.loginFooter()}
                            </>
                        )}
                    </div>
                </section>
                <SoftwareVersion />
            </div>
        );
    }

    private handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value.trim();
        void this.setState(newState);
    }

    private async forgetPasswordSubmit() {
        await this.setState({ submitted: true });
        const url = '/api/password/sendResetPasswordLink';
        const data = {
            emailAddress: this.state.userEmail,
        };
        const result = await fetchPost(url, {}, data, ['EMAIL_NOT_VALID', 'FAILED_SENDING_MAIL']);
        if (result.status !== 200) {
            await this.setState({ code: result.errorCode, validationError: true, response: true, submitted: false });
        } else {
            await this.setState({ code: '', validationError: false, response: true, submitted: false });
        }
    }

    private async newPasswordSubmit() {
        await this.setState({ submitted: true, response: false });
        const url = '/api/password/setPassword';
        const data = {
            newPassWordToken: this.props.passwordToken,
            password: this.state.password,
            repeatPassword: this.state.repeatPassword,
        };
        const result = await fetchPost(url, {}, data, [
            'PASSWORD_LINK_EXPIRED',
            'PASSWORD_TOO_SHORT',
            'PASSWORD_NO_PUNCTUATION',
            'PASSWORD_NOT_THE_SAME',
            'NOT_ALLOWED',
        ]);
        if (result.status !== 200) {
            await this.setState({ code: result.errorCode, validationError: true, response: true });
        } else {
            await this.setState({ code: '', validationError: false, response: true });
            setTimeout(async () => {
                await this.setState({ form: 'login', submitted: false, response: false, validationError: false });
            }, 3000);
        }
    }

    private async validateToken() {
        const url = `/api/password/validateToken/${this.props.passwordToken}`;
        const result = await fetchGet(url, {}, ['PASSWORD_LINK_EXPIRED']);
        if (result.status !== 200) {
            await this.setState({ code: result.errorCode, tokenError: true });
        } else {
            await this.setState({ code: '', validationError: false, userEmail: result.json.emailAddress });
        }
    }

    private loginFooter() {
        return (
            <div className="login-links">
                <p>
                    <LinkButton className="active" onClick={async () => this.setState({ form: 'login', submitted: false, validationError: false })}>
                        Login
                    </LinkButton>
                </p>
                <p>
                    <a href="forgot-password-support.html">Service desk</a>
                </p>
            </div>
        );
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const LoginContainer = translate()(LoginContainerComponent);
export { LoginContainer };
